import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    TextField,
    Grid,
    MenuItem,
    Checkbox,
    Stack,
    Autocomplete,
    Tooltip,
    alpha
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    History as HistoryIcon,
    Save as SaveIcon,
    Clear as ClearIcon,
    Payment as PaymentIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchFactures } from '../../data/slices/factureSlice';
import { fetchClients } from '../../data/slices/clientStore';
import { fetchPaiments } from '../../data/slices/paiementsSlice';
import { COLORS } from '../../settings/themeSettings';
import PageHeader from '../../components/layout/PageHeader';
import { formatCurrency, formatDate, parseAnyDate } from '../../utils/compteClientEngine';
import RecuEncaissementModal from './components/RecuEncaissementModal';
import HistoriqueEncaissementsModal from './components/HistoriqueEncaissementsModal';
import dayjs from 'dayjs';

const MODES_PAIEMENT = [
    { value: 'Espèces', label: 'Espèces' },
    { value: 'Chèque', label: 'Chèque' },
    { value: 'Virement', label: 'Virement' },
    { value: 'Carte', label: 'Carte Bancaire' },
    { value: 'Autres', label: 'Autres (Compensation, Échange...)' }
];

const SaisieEncaissementPage = () => {
    const dispatch = useDispatch();
    const { factures } = useSelector((state) => state.factures);
    const { clients } = useSelector((state) => state.clients);

    // Formulaire d'encaissement (Section 4.2)
    const [selectedClient, setSelectedClient] = useState(null);
    const [dateEncaissement, setDateEncaissement] = useState(dayjs().format('YYYY-MM-DD'));
    const [modePaiement, setModePaiement] = useState('Espèces');
    const [autreModeDetails, setAutreModeDetails] = useState('');
    const [referencePaiement, setReferencePaiement] = useState('');
    const [montantEncaisseInput, setMontantEncaisseInput] = useState('');

    // Factures cochées et montants affectés { [factureId]: { selected: boolean, affecte: number } }
    const [affectations, setAffectations] = useState({});

    // Modales Reçu et Historique
    const [openRecuModal, setOpenRecuModal] = useState(false);
    const [currentRecuData, setCurrentRecuData] = useState(null);
    const [openHistoriqueModal, setOpenHistoriqueModal] = useState(false);

    // Historique local mémorisé des encaissements de la session
    const [historiqueLocal, setHistoriqueLocal] = useState(() => {
        try {
            const saved = localStorage.getItem('aft_historique_encaissements');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    const location = useLocation();

    useEffect(() => {
        dispatch(fetchFactures());
        dispatch(fetchClients());
        dispatch(fetchPaiments());
    }, [dispatch]);

    useEffect(() => {
        if (location.state?.client && Array.isArray(clients) && clients.length > 0) {
            const passed = location.state.client;
            const found = clients.find(c =>
                (passed.id && (c.id === passed.id || c._id === passed.id)) ||
                (passed.code && c.code === passed.code) ||
                (passed.nomComplet && c.nomComplet === passed.nomComplet) ||
                (passed.libelle && c.libelle === passed.libelle)
            );
            if (found) {
                setSelectedClient(found);
            } else if (typeof passed === 'object') {
                setSelectedClient(passed);
            }
        }
    }, [location.state, clients]);

    // Factures ouvertes / non soldées du client sélectionné
    const clientFacturesOuvertes = useMemo(() => {
        if (!selectedClient || !Array.isArray(factures)) return [];

        const cId = selectedClient.id || selectedClient.code;
        const cName = selectedClient.libelle || selectedClient.nomComplet || selectedClient.nom || '';

        return factures.filter(fact => {
            const fc = fact.toClient || fact.client;
            const matchesId = cId && (String(fc?.id) === String(cId) || String(fc?.code) === String(cId));
            const matchesName = cName && (
                (typeof fc === 'string' && fc.toLowerCase() === cName.toLowerCase()) ||
                (fact.nomClient && fact.nomClient.toLowerCase() === cName.toLowerCase()) ||
                (fc?.libelle && fc.libelle.toLowerCase() === cName.toLowerCase())
            );

            if (!matchesId && !matchesName) return false;

            // Facture ouverte si solde != 0
            const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
            let paye = 0;
            if (Array.isArray(fact.paiements)) paye = fact.paiements.reduce((s, p) => s + Number(p.montant || 0), 0);
            else if (fact.dejaPaye) paye = Number(fact.dejaPaye);
            const solde = fact.solde !== undefined ? Number(fact.solde) : (montant - paye);

            return Math.abs(solde) > 0.01;
        }).map(fact => {
            const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
            let paye = 0;
            if (Array.isArray(fact.paiements)) paye = fact.paiements.reduce((s, p) => s + Number(p.montant || 0), 0);
            else if (fact.dejaPaye) paye = Number(fact.dejaPaye);
            const solde = fact.solde !== undefined ? Number(fact.solde) : (montant - paye);

            const factDate = parseAnyDate(fact.date || fact.createdAt);
            const clientDelai = Number(selectedClient?.delaiPaiement || 30);
            const echeance = fact.echeance ? parseAnyDate(fact.echeance) : (factDate ? factDate.add(clientDelai, 'day') : dayjs());
            const isLate = dayjs().isAfter(echeance, 'day');

            return {
                ...fact,
                computedMontant: montant,
                computedPaye: paye,
                computedSolde: solde,
                echeanceStr: echeance ? echeance.format('DD/MM/YYYY') : '-',
                isLate
            };
        });
    }, [selectedClient, factures]);

    // Réinitialisation des affectations quand le client change
    useEffect(() => {
        setAffectations({});
    }, [selectedClient]);

    // Gestion de la sélection d'une facture (Section 4.2)
    const handleToggleFacture = (fact) => {
        const factId = fact.id || fact.numeroFacture;
        setAffectations(prev => {
            const current = prev[factId];
            if (current && current.selected) {
                // Décocher
                const next = { ...prev };
                delete next[factId];
                return next;
            } else {
                // Cocher -> auto-remplir le montant affecté avec le solde dû
                return {
                    ...prev,
                    [factId]: {
                        selected: true,
                        affecte: fact.computedSolde
                    }
                };
            }
        });
    };

    // Modification manuelle du montant affecté
    const handleAffecteChange = (factId, value) => {
        const numVal = parseFloat(value) || 0;
        setAffectations(prev => ({
            ...prev,
            [factId]: {
                selected: true,
                affecte: numVal
            }
        }));
    };

    // Calculs de totalisation et d'équilibre (Section 4.2 & 4.4)
    const montantEncaisseNum = parseFloat(montantEncaisseInput) || 0;

    const totalAffecte = useMemo(() => {
        return Object.values(affectations).reduce((sum, item) => {
            if (item.selected) {
                return sum + (Number(item.affecte) || 0);
            }
            return sum;
        }, 0);
    }, [affectations]);

    const totalFacturesSelectionneesSolde = useMemo(() => {
        return clientFacturesOuvertes.reduce((sum, fact) => {
            const factId = fact.id || fact.numeroFacture;
            if (affectations[factId]?.selected) {
                return sum + fact.computedSolde;
            }
            return sum;
        }, 0);
    }, [clientFacturesOuvertes, affectations]);

    const ecart = montantEncaisseNum - totalAffecte;
    const isEquilibre = Math.abs(ecart) < 0.01 && montantEncaisseNum > 0 && totalAffecte > 0;

    // Validation de l'encaissement (Section 4.3)
    const handleValiderEncaissement = () => {
        if (!isEquilibre || !selectedClient) return;

        const annee = dayjs().year();
        const randNum = String(Math.floor(1000 + Math.random() * 9000));
        const numRecu = `E-${annee}-${randNum}`;

        const lignesRecu = clientFacturesOuvertes
            .filter(fact => affectations[fact.id || fact.numeroFacture]?.selected)
            .map(fact => ({
                factureId: fact.id,
                numeroFacture: fact.numeroFacture || `F-${fact.id}`,
                dateFacture: fact.date || fact.createdAt,
                montantAffecte: affectations[fact.id || fact.numeroFacture]?.affecte || 0,
                prestation: fact.commentaire || fact.typeFacture || 'Prestation'
            }));

        const recuObj = {
            id: Date.now(),
            numeroRecu: numRecu,
            client: selectedClient,
            clientName: selectedClient.libelle || selectedClient.nomComplet || selectedClient.nom || 'Client',
            dateEncaissement: dateEncaissement,
            modePaiement: modePaiement === 'Autres' ? `Autres (${autreModeDetails || 'Divers'})` : modePaiement,
            reference: referencePaiement,
            montantEncaisse: montantEncaisseNum,
            utilisateur: 'Agent Comptable AFT',
            lignes: lignesRecu,
            createdAt: new Date().toISOString()
        };

        // Sauvegarder dans l'historique
        const updatedHist = [recuObj, ...historiqueLocal];
        setHistoriqueLocal(updatedHist);
        try {
            localStorage.setItem('aft_historique_encaissements', JSON.stringify(updatedHist));
        } catch (e) {
            console.error('Erreur sauvegarde historique encaissement:', e);
        }

        // Ouvrir le reçu pour impression/consultation
        setCurrentRecuData(recuObj);
        setOpenRecuModal(true);

        // Réinitialiser les saisies
        setMontantEncaisseInput('');
        setReferencePaiement('');
        setAffectations({});
    };

    const handleReset = () => {
        setSelectedClient(null);
        setMontantEncaisseInput('');
        setReferencePaiement('');
        setModePaiement('Espèces');
        setAutreModeDetails('');
        setAffectations({});
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader
                title="Saisie encaissements"
                subtitle="Règlement des factures clients (lot ou unitaire) et émission de reçus"
                icon={PaymentIcon}
                breadcrumbs={[
                    { label: 'Facturation', path: '/facturation/gestion-client' },
                    { label: 'Saisie encaissements', path: '/facturation/encaissements' }
                ]}
                actions={[
                    {
                        label: 'Historique des encaissements',
                        icon: <HistoryIcon />,
                        onClick: () => setOpenHistoriqueModal(true),
                        variant: 'outlined',
                        color: 'primary'
                    }
                ]}
            />

            {/* FORMULAIRE DE SAISIE DE L'ENCAISSEMENT (SECTION 4.2) */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Encaissement de facture — Paramètres du règlement
                </Typography>

                <Grid container spacing={2.5}>
                    {/* Client */}
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={Array.isArray(clients) ? clients : []}
                            getOptionLabel={(option) => {
                                if (!option) return '';
                                const name = option.libelle || option.nomComplet || option.nom || option.code || '';
                                const code = option.code ? ` (${option.code})` : '';
                                return `${name}${code}`;
                            }}
                            isOptionEqualToValue={(option, val) => (option?.id && val?.id ? option.id === val.id : option === val)}
                            renderOption={(props, option, { index }) => {
                                const { key, ...otherProps } = props;
                                const uniqueKey = option.id || option._id || (option.code ? `client-code-${option.code}-${index}` : `client-idx-${index}`);
                                const name = option.libelle || option.nomComplet || option.nom || option.code || 'Client';
                                const phone = option.telephone ? ` — ${option.telephone}` : '';
                                const code = option.code ? ` [${option.code}]` : '';
                                return (
                                    <li key={uniqueKey} {...otherProps}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                                {name}{code}
                                            </Typography>
                                            {phone && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {phone}
                                                </Typography>
                                            )}
                                        </Box>
                                    </li>
                                );
                            }}
                            value={selectedClient}
                            onChange={(e, val) => setSelectedClient(val)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="Client (Recherche dynamique)"
                                    placeholder="Sélectionner le client..."
                                    required
                                />
                            )}
                        />
                    </Grid>

                    {/* Date d'encaissement */}
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            label="Date d'encaissement"
                            InputLabelProps={{ shrink: true }}
                            value={dateEncaissement}
                            onChange={(e) => setDateEncaissement(e.target.value)}
                        />
                    </Grid>

                    {/* Mode de paiement */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Mode de paiement"
                            value={modePaiement}
                            onChange={(e) => setModePaiement(e.target.value)}
                        >
                            {MODES_PAIEMENT.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </TextField>
                    </Grid>

                    {/* Référence */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Référence (N° chèque, virement...)"
                            placeholder="Ex: CHQ-88219"
                            value={referencePaiement}
                            onChange={(e) => setReferencePaiement(e.target.value)}
                        />
                    </Grid>

                    {/* Précision si Autres */}
                    {modePaiement === 'Autres' && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Préciser la nature du règlement"
                                placeholder="Compensation, échange de marchandise, etc."
                                value={autreModeDetails}
                                onChange={(e) => setAutreModeDetails(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {/* Montant encaissé */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            type="number"
                            fullWidth
                            size="small"
                            label="Montant encaissé (CFA)"
                            placeholder="Saisi une seule fois..."
                            value={montantEncaisseInput}
                            onChange={(e) => setMontantEncaisseInput(e.target.value)}
                            required
                            InputProps={{
                                sx: { fontWeight: 'bold', fontSize: '1.05rem', color: COLORS.primary }
                            }}
                        />
                    </Grid>

                    {/* Total des factures sélectionnées en continu */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                bgcolor: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                Total sélectionné (soldes dus) :
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                {formatCurrency(totalFacturesSelectionneesSolde)} CFA
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>

            {/* TABLEAU DES FACTURES OUVERTES DU CLIENT (SECTION 4.2 & 4.3) */}
            {selectedClient && (
                <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase' }}>
                            Factures ouvertes du client ({clientFacturesOuvertes.length})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Cochez les factures concernées. Le montant affecté est modifiable si paiement partiel ou trop-perçu.
                        </Typography>
                    </Stack>

                    <Box sx={{ border: '1px solid #0f172a', borderRadius: '8px', overflow: 'hidden' }}>
                        {/* En-tête figée */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '80px 150px 120px 130px 140px 140px 160px', bgcolor: '#0f172a', color: 'white', p: 1.2, fontWeight: 'bold', fontSize: '0.85rem' }}>
                            <Box sx={{ textAlign: 'center' }}>Sélection</Box>
                            <Box>N° Facture</Box>
                            <Box>Date</Box>
                            <Box>Échéance</Box>
                            <Box sx={{ textAlign: 'right' }}>Montant</Box>
                            <Box sx={{ textAlign: 'right' }}>Solde dû</Box>
                            <Box sx={{ textAlign: 'right', pr: 1 }}>Montant affecté</Box>
                        </Box>

                        <Box sx={{ maxHeight: '45vh', overflowY: 'auto' }}>
                            {clientFacturesOuvertes.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                    Toutes les factures de ce client sont déjà soldées.
                                </Box>
                            ) : (
                                clientFacturesOuvertes.map((fact, idx) => {
                                    const factId = fact.id || fact.numeroFacture;
                                    const isSelected = !!affectations[factId]?.selected;
                                    const affecteVal = affectations[factId]?.affecte ?? '';
                                    const isAvoir = fact.computedSolde < 0;

                                    return (
                                        <Box
                                            key={factId}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '80px 150px 120px 130px 140px 140px 160px',
                                                p: 1.2,
                                                alignItems: 'center',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid #f1f5f9',
                                                bgcolor: isSelected ? alpha(COLORS.primary, 0.08) : (idx % 2 === 0 ? 'white' : '#f8fafc')
                                            }}
                                        >
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={isSelected}
                                                    onChange={() => handleToggleFacture(fact)}
                                                />
                                            </Box>
                                            <Box sx={{ fontWeight: 700, color: COLORS.primary }}>
                                                {fact.numeroFacture || `F-${fact.id}`}
                                            </Box>
                                            <Box sx={{ color: 'text.secondary' }}>{formatDate(fact.date || fact.createdAt)}</Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <span>{fact.echeanceStr}</span>
                                                {fact.isLate && (
                                                    <Tooltip title="Facture échue">
                                                        <WarningIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Box sx={{ textAlign: 'right', fontWeight: 600 }}>
                                                {formatCurrency(fact.computedMontant)}
                                            </Box>
                                            <Box sx={{ textAlign: 'right', fontWeight: 700, color: isAvoir ? '#2563eb' : (fact.isLate ? '#dc2626' : '#0f172a') }}>
                                                {formatCurrency(fact.computedSolde)}
                                                {isAvoir && <Typography variant="caption" display="block" color="primary">(Avoir/Trop-perçu)</Typography>}
                                            </Box>
                                            <Box sx={{ pr: 1 }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    fullWidth
                                                    value={affecteVal}
                                                    disabled={!isSelected}
                                                    onChange={(e) => handleAffecteChange(factId, e.target.value)}
                                                    placeholder="Montant affecté"
                                                    InputProps={{
                                                        sx: {
                                                            height: 32,
                                                            fontSize: '0.85rem',
                                                            fontWeight: 'bold',
                                                            textAlign: 'right'
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>

                    {/* BANDEAU DE CONTRÔLE D'ÉQUILIBRE STRICT (SECTION 4.2 & 4.3) */}
                    <Box sx={{ mt: 3, p: 2, borderRadius: '8px', bgcolor: isEquilibre ? '#f0fdf4' : '#fef2f2', border: `1px solid ${isEquilibre ? '#86efac' : '#fecaca'}` }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: isEquilibre ? '#166534' : '#991b1b' }}>
                                    {isEquilibre ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <CheckCircleIcon color="success" /> Équilibre parfait : Montant encaissé = Total affecté ({formatCurrency(montantEncaisseNum)} CFA)
                                        </span>
                                    ) : (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <WarningIcon color="error" /> Contrôle des montants : Montant encaissé ({formatCurrency(montantEncaisseNum)} CFA) vs Total affecté ({formatCurrency(totalAffecte)} CFA)
                                            {Math.abs(ecart) > 0.01 && <strong> — Écart : {formatCurrency(Math.abs(ecart))} CFA</strong>}
                                        </span>
                                    )}
                                </Typography>
                            </Box>

                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    onClick={handleReset}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Annuler
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<SaveIcon />}
                                    disabled={!isEquilibre}
                                    onClick={handleValiderEncaissement}
                                    sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}
                                >
                                    Valider l'encaissement
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Paper>
            )}

            {/* MODALE DU REÇU OFFICIEL IMPRIMABLE (SECTION 4.7) */}
            <RecuEncaissementModal
                open={openRecuModal}
                onClose={() => setOpenRecuModal(false)}
                recuData={currentRecuData}
            />

            {/* MODALE DE L'HISTORIQUE DES ENCAISSEMENTS (SECTION 4.6) */}
            <HistoriqueEncaissementsModal
                open={openHistoriqueModal}
                onClose={() => setOpenHistoriqueModal(false)}
                encaissements={historiqueLocal}
                onSelectRecu={(recu) => {
                    setCurrentRecuData(recu);
                    setOpenRecuModal(true);
                }}
            />
        </Box>
    );
};

export default SaisieEncaissementPage;
